/**
 * Search Bar Component
 *
 * A search input with submit button for searching posts.
 */

import { useState, useCallback, KeyboardEvent } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, X } from 'lucide-react'

interface SearchBarProps {
  value?: string
  onSearch: (query: string) => void
  placeholder?: string
  autoFocus?: boolean
  className?: string
}

export function SearchBar({
  value = '',
  onSearch,
  placeholder = 'Search...',
  autoFocus = false,
  className = '',
}: SearchBarProps) {
  const [query, setQuery] = useState(value)

  const handleSubmit = useCallback(() => {
    const trimmed = query.trim()
    if (trimmed) {
      onSearch(trimmed)
    }
  }, [query, onSearch])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const handleClear = useCallback(() => {
    setQuery('')
  }, [])

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="pl-10 pr-10"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <Button onClick={handleSubmit} size="sm">
        Search
      </Button>
    </div>
  )
}
